import React, { type ReactNode, useContext, useMemo } from 'react';

import {
  Box,
  ButtonGroup,
  Container,
  type ContainerProps,
  Flex,
  type FlexProps,
  HStack,
  Stack,
} from '@chakra-ui/react';
import useMeasure from 'react-use-measure';

import { themeBreakpoints } from '@/utils/responsive';

import {
  type SurveyLayoutContextNavDisplayed,
  useSurveyLayoutContext,
  useSurveyLayoutHideNav,
} from './SurveyLayout';
import { SURVEY_NAV_BAR_HEIGHT } from './constants';

type SurveyLayoutPageContextValue = {
  nav: React.ReactNode;
  noContainer: boolean;
  containerMaxWidth: ContainerProps['maxW'];
};

const SurveyLayoutPageContext =
  React.createContext<SurveyLayoutPageContextValue | null>(null);

const useSurveyLayoutPageContext = () => {
  const context = useContext(SurveyLayoutPageContext);
  if (context === null) {
    throw new Error('Missing parent <SurveyLayoutPage> component');
  }
  return context;
};

const PageContainer = ({ children, maxW, ...rest }: ContainerProps) => {
  const { noContainer, containerMaxWidth } = useSurveyLayoutPageContext();

  if (noContainer) return <>{children}</>;
  return (
    <Container
      display="flex"
      flexDirection="column"
      flex="1"
      w="full"
      maxW={maxW ?? containerMaxWidth}
      {...rest}
      sx={{
        [themeBreakpoints.down('lg')]: {
          paddingRight: 0,
          paddingLeft: 0,
        },
      }}
    >
      {children}
    </Container>
  );
};

type SurveyLayoutPageTopBarProps = FlexProps & {
  leftActions?: ReactNode;
  rightActions?: ReactNode;
  isFixed?: boolean;
  isConfirmDiscardChanges?: boolean;
  containerMaxWidth?: ContainerProps['maxW'];
};

export const SurveyLayoutPageTopBar = ({
  children,
  leftActions,
  rightActions,
  isFixed = true,
  containerMaxWidth,
  ...rest
}: SurveyLayoutPageTopBarProps) => {
  const { navDisplayed } = useSurveyLayoutContext();
  const [ref, { height }] = useMeasure();

  return (
    <>
      {isFixed && <Box h={`${height}px`} />}
      <Flex
        zIndex={2}
        direction="column"
        py={3}
        boxShadow="layout"
        bg="white"
        ref={ref}
        borderBottom="1px solid transparent"
        borderBottomColor="gray.100"
        {...(isFixed
          ? {
              top: navDisplayed ? SURVEY_NAV_BAR_HEIGHT : '0',
              position: 'fixed',
              right: '0',
              left: '0',
            }
          : {})}
        {...rest}
      >
        <Box w="full" h="0" pb="safe-top" />
        <PageContainer maxW={containerMaxWidth}>
          <HStack spacing="4">
            {!!leftActions && (
              <ButtonGroup size="sm" spacing={3}>
                {leftActions}
              </ButtonGroup>
            )}
            <Box flex="1">{children}</Box>
            {!!rightActions && (
              <ButtonGroup size="sm" spacing={3}>
                {rightActions}
              </ButtonGroup>
            )}
          </HStack>
        </PageContainer>
      </Flex>
    </>
  );
};

type SurveyLayoutPageContentProps = FlexProps & {
  onBack?(): void;
  showBack?: boolean;
  containerMaxWidth?: ContainerProps['maxW'];
};

export const SurveyLayoutPageContent = ({
  children,
  containerMaxWidth,
  ...rest
}: SurveyLayoutPageContentProps) => {
  const { nav } = useSurveyLayoutPageContext();
  return (
    <Flex
      position="relative"
      zIndex="1"
      direction="column"
      flex="1"
      py={{ lg: '4' }}
      {...rest}
    >
      <PageContainer maxW={containerMaxWidth} pb={16}>
        <Stack
          direction={{ base: 'column', lg: 'row' }}
          spacing={{ base: '2', lg: '8' }}
          flex="1"
        >
          {nav && (
            <Flex
              direction="column"
              minW="0"
              w={{ lg: '20rem' }}
              borderBottom={{
                base: '1px solid',
                lg: 'none',
              }}
              borderBottomColor={{
                base: 'gray.50',
                lg: 'transparent',
              }}
            >
              {nav}
            </Flex>
          )}

          <Flex
            direction="column"
            flex="1"
            minW="0"
            borderTop={{
              base: '1px solid',
              lg: 'none',
            }}
            borderTopColor={{
              base: 'gray.50',
              lg: 'transparent',
            }}
          >
            {children}
          </Flex>
        </Stack>
      </PageContainer>
      <Box w="full" h="0" pb="safe-bottom" />
    </Flex>
  );
};

type SurveyLayoutPageBottomBarProps = FlexProps & {
  containerMaxWidth?: ContainerProps['maxW'];
};

export const SurveyLayoutPageBottomBar = ({
  children,
  containerMaxWidth,
  ...rest
}: SurveyLayoutPageBottomBarProps) => {
  const [ref, { height }] = useMeasure();

  return (
    <>
      <Box h={`${height}px`} />
      <Flex
        zIndex="3"
        ref={ref}
        direction="column"
        mt="auto"
        position="fixed"
        bottom="0"
        insetStart="0"
        insetEnd="0"
        py="2"
        boxShadow="layout"
        bg="white"
        borderTop="1px solid transparent"
        {...rest}
      >
        <PageContainer maxW={containerMaxWidth}>{children}</PageContainer>
        <Box w="full" h="0" pb="safe-bottom" />
      </Flex>
    </>
  );
};

type SurveyLayoutPageProps = FlexProps & {
  showNavBar?: SurveyLayoutContextNavDisplayed;
  containerMaxWidth?: ContainerProps['maxW'];
  noContainer?: boolean;
  nav?: React.ReactNode;
};

export const SurveyLayoutPage = ({
  showNavBar = true,
  noContainer = false,
  containerMaxWidth = 'container.xl',
  nav = null,
  ...rest
}: SurveyLayoutPageProps) => {
  useSurveyLayoutHideNav(showNavBar);

  const value = useMemo(
    () => ({
      nav,
      noContainer,
      containerMaxWidth,
    }),
    [containerMaxWidth, noContainer, nav]
  );

  return (
    <SurveyLayoutPageContext.Provider value={value}>
      <Flex direction="column" flex="1" position="relative" {...rest} />
    </SurveyLayoutPageContext.Provider>
  );
};
